import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Lock, Share2, Trophy, Utensils } from "lucide-react";
import { api, USER_ID } from "../api";
import { toast } from "sonner";
import { badgeToast, melaToast, MelaCoin } from "./BadgesSheet";
import { coinRain } from "./coinRain";
import { shareCricketWin } from "../share";
import { flickThwip, whipCrack, knockPing, cheerArp, clonk } from "./sfx";

const W = 420, H = 470;
const PITCH_Y = 292;              // ground line of the pitch
const BAT_X = 118, BOWL_X = 332;  // batter left crease, bowler right crease
const RELEASE = { x: 322, y: 206 };
const CONTACT_X = 134;
const RM = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
const INTRO_KEY = "xp_cricket_intro";
const DIFFS = [
  { key: "easy", name: "GULLY ROOKIE", desc: "Slow bowling, big window — Regal Coins only" },
  { key: "medium", name: "MELA PLAYER", desc: "Mixed pace & spin — coins and badges" },
  { key: "hard", name: "PUNJAB PRO", desc: "Fast, swinging bowling — for the true ustaads" },
];
const COMMENT = {
  6: ["That one is heading into the mela!", "Sends it over the boundary!", "MASSIVE! Into the jalebi stall!"],
  4: ["What a shot!", "Straight through the gap!", "Races away past the tractor!"],
  run: ["Quick running between the wickets!", "Tidy placement, they scamper through!"],
  dot: ["No run — good ball.", "Beaten! Nothing off that one."],
  out: ["Bowled! The stumps are flying!", "Caught! The crowd goes silent...", "That's the end of that!"],
};
const PA_POPS = ["Chak De!", "Balle Balle!", "Kya Shot Hai!", "Shabaash!"];

const judge = (offset, dir, del, power, windowMs) => {
  const a = Math.abs(offset);
  const band = a <= windowMs * 0.38 ? "perfect" : a <= windowMs ? "good" : a <= windowMs * 1.9 ? "weak" : "miss";
  const label = band === "perfect" ? "PERFECT" : offset < 0 ? (band === "miss" ? "TOO EARLY" : "EARLY") : (band === "miss" ? "TOO LATE" : "LATE");
  const timing = band === "perfect" ? "perfect" : band;
  const match = dir === "loft" || dir === "straight" ? del.line === "straight" || dir === "loft" : dir === del.line;
  const r = Math.random();
  let runs = 0, out = false, how = "";
  if (band === "miss") {
    if (dir === "defend") runs = 0;
    else if (del.type === "yorker" || del.type === "full") { out = r < 0.6; how = "BOWLED"; }
    else if (del.line === "straight" && dir === "leg") { out = r < 0.5; how = "LBW"; }
    else { out = r < 0.2; how = "BOWLED"; }
  } else if (dir === "defend") {
    runs = r < 0.4 ? 1 : 0;
  } else if (band === "weak") {
    if (!match && power > 0.6 && r < 0.3) { out = true; how = "CAUGHT"; }
    else runs = r < 0.5 ? 1 : 0;
  } else if (band === "good") {
    if (!match) {
      if (power > 0.65 && r < 0.28) { out = true; how = "CAUGHT"; }
      else runs = r < 0.6 ? 1 : 0;
    } else if (del.type === "bouncer" && dir !== "loft") runs = 1;
    else runs = power > 0.72 ? (r < 0.45 ? 4 : r < 0.75 ? 2 : 3) : (r < 0.6 ? 1 : 2);
  } else {
    if (dir === "loft") runs = power >= 0.5 ? (r < 0.72 ? 6 : 4) : (r < 0.5 ? 4 : 2);
    else if (match) runs = r < 0.62 ? 4 : (r < 0.85 ? 2 : 3);
    else if (power > 0.7 && r < 0.15) { out = true; how = "CAUGHT"; }
    else runs = r < 0.5 ? 2 : 1;
  }
  return { runs, out, how, timing: timing === "good" || timing === "weak" ? "good" : timing, label };
};

// ball y along its flight: release -> bounce on the pitch -> up to contact height
const ballY = (p, del, contactY) => {
  const bounceAt = del.type === "yorker" ? 0.88 : del.type === "full" ? 0.78 : 0.58;
  if (p <= bounceAt) {
    const q = p / bounceAt;
    let y = RELEASE.y + (PITCH_Y - 6 - RELEASE.y) * (q * q * 0.4 + q * 0.6);
    if (del.type === "swing") y += Math.sin(q * Math.PI) * 16;
    return y;
  }
  const q = (p - bounceAt) / (1 - bounceAt);
  const peak = del.type === "bouncer" ? contactY - 26 : contactY;
  return (PITCH_Y - 6) + (peak - (PITCH_Y - 6)) * Math.sin(q * Math.PI / 2);
};

export const CricketClash = ({ refresh, ustaadUnlocked }) => {
  const canvasRef = useRef(null);
  const bgRef = useRef(null);
  const g = useRef(null);
  const rafRef = useRef(0);
  const imgs = useRef({});
  const [stage, setStage] = useState(() => (localStorage.getItem(INTRO_KEY) === "1" ? "pick" : "intro"));
  const [team, setTeam] = useState("urban");
  const [diff, setDiff] = useState("easy");
  const [busy, setBusy] = useState(false);
  const [hud, setHud] = useState({ score: 0, wickets: 0, balls: 0, target: 0, feedback: "", comment: "", golden: false });
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    ["urban_bat", "urban_bowl", "regal_bat", "regal_bowl"].forEach((n) => {
      const im = new Image();
      im.src = `/cricket/${n}.png`;
      imgs.current[n] = im;
    });
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const drawBg = () => {
    const c = document.createElement("canvas");
    c.width = W; c.height = H;
    const x = c.getContext("2d");
    const sky = x.createLinearGradient(0, 0, 0, H * 0.4);
    sky.addColorStop(0, "#231433");
    sky.addColorStop(0.7, "#5A2B3F");
    sky.addColorStop(1, "#C97A3A");
    x.fillStyle = sky; x.fillRect(0, 0, W, H * 0.42);
    x.fillStyle = "#2E5D33"; x.fillRect(0, H * 0.4, W, H * 0.6);
    x.fillStyle = "#356B3B";
    x.beginPath(); x.ellipse(W / 2, H * 0.62, W * 0.62, H * 0.42, 0, 0, Math.PI * 2); x.fill();
    x.strokeStyle = "#F5F0E0"; x.lineWidth = 2.5;
    x.beginPath(); x.ellipse(W / 2, H * 0.62, W * 0.58, H * 0.395, 0, 0, Math.PI * 2); x.stroke();
    for (let i = 0; i < 40; i++) {
      const a = (i / 40) * Math.PI * 2;
      x.fillStyle = i % 2 ? "#F09819" : "#E25822";
      x.beginPath(); x.arc(W / 2 + Math.cos(a) * W * 0.58, H * 0.62 + Math.sin(a) * H * 0.395, 3, 0, Math.PI * 2); x.fill();
    }
    // horizontal pitch strip between the two creases
    x.fillStyle = "#C9A96A";
    x.beginPath();
    x.moveTo(BAT_X - 30, PITCH_Y - 26); x.lineTo(BOWL_X + 28, PITCH_Y - 26);
    x.lineTo(BOWL_X + 36, PITCH_Y + 6); x.lineTo(BAT_X - 38, PITCH_Y + 6);
    x.closePath(); x.fill();
    x.strokeStyle = "rgba(255,255,255,0.75)"; x.lineWidth = 1.5;
    x.beginPath(); x.moveTo(BAT_X + 16, PITCH_Y - 26); x.lineTo(BAT_X + 12, PITCH_Y + 6); x.stroke();
    x.beginPath(); x.moveTo(BOWL_X - 16, PITCH_Y - 26); x.lineTo(BOWL_X - 12, PITCH_Y + 6); x.stroke();
    // tractor (left, outside boundary)
    x.fillStyle = "#7A2E1D";
    x.fillRect(10, H * 0.42, 40, 16); x.fillRect(38, H * 0.40, 14, 12);
    x.fillStyle = "#1A1A1A";
    x.beginPath(); x.arc(20, H * 0.46 + 14, 8, 0, Math.PI * 2); x.fill();
    x.beginPath(); x.arc(44, H * 0.46 + 15, 5, 0, Math.PI * 2); x.fill();
    ["JALEBI", "CHAI", "KULFI"].forEach((s, i) => {
      const sx = W - 78, sy = H * 0.24 + i * 26;
      x.fillStyle = "#3A2415"; x.fillRect(sx, sy + 7, 66, 16);
      x.fillStyle = i === 0 ? "#E25822" : i === 1 ? "#D4AF37" : "#8FBF9F";
      x.beginPath(); x.moveTo(sx - 3, sy + 8); x.lineTo(sx + 33, sy - 2); x.lineTo(sx + 69, sy + 8); x.fill();
      x.fillStyle = "#F5E9C9"; x.font = "900 8px sans-serif"; x.textAlign = "center";
      x.fillText(s, sx + 33, sy + 18);
    });
    x.fillStyle = "#B8860B"; x.fillRect(14, H * 0.50, 52, 13);
    x.fillStyle = "#4A1520"; x.fillRect(W - 66, H * 0.50, 52, 13);
    for (let r = 0; r < 2; r++) for (let i = 0; i < 5; i++) {
      x.fillStyle = "#6B4A2B";
      x.fillRect(24 + i * 78, H - 44 + r * 20, 48, 6);
    }
    bgRef.current = c;
  };

  const draw = useCallback((now) => {
    const st = g.current;
    const cv = canvasRef.current;
    if (!st || !cv) return;
    const x = cv.getContext("2d");
    if (!bgRef.current) drawBg();
    x.clearRect(0, 0, W, H);
    x.drawImage(bgRef.current, 0, 0);
    // ferris wheel
    const fa = now / 4000;
    x.strokeStyle = "rgba(240,199,66,0.85)"; x.lineWidth = 1.5;
    x.beginPath(); x.arc(56, 84, 36, 0, Math.PI * 2); x.stroke();
    for (let i = 0; i < 8; i++) {
      const a = fa + (i / 8) * Math.PI * 2;
      x.beginPath(); x.moveTo(56, 84); x.lineTo(56 + Math.cos(a) * 36, 84 + Math.sin(a) * 36); x.stroke();
      x.fillStyle = ["#E25822", "#D4AF37", "#2E9E5B", "#4FA3D1"][i % 4];
      x.beginPath(); x.arc(56 + Math.cos(a) * 36, 84 + Math.sin(a) * 36, 3.4, 0, Math.PI * 2); x.fill();
    }
    x.beginPath(); x.moveTo(40, 122); x.lineTo(56, 84); x.lineTo(72, 122); x.stroke();
    // bunting + lights
    for (let i = 0; i < 12; i++) {
      const bx = 10 + i * 34;
      x.fillStyle = ["#E25822", "#F0C742", "#2E9E5B", "#C0392B"][i % 4];
      x.beginPath(); x.moveTo(bx, 8); x.lineTo(bx + 8, 22); x.lineTo(bx + 16, 8); x.fill();
      x.globalAlpha = 0.5 + 0.5 * Math.abs(Math.sin(now / 700 + i));
      x.fillStyle = "#FFE9A0";
      x.beginPath(); x.arc(bx + 8, 30, 2.2, 0, Math.PI * 2); x.fill();
      x.globalAlpha = 1;
    }
    // pinwheels
    [78, W - 96].forEach((px, i) => {
      x.save(); x.translate(px, H - 52); x.rotate(RM ? 0 : now / (300 + i * 120));
      x.strokeStyle = i ? "#4FA3D1" : "#E25822"; x.lineWidth = 3;
      for (let k = 0; k < 4; k++) { x.rotate(Math.PI / 2); x.beginPath(); x.moveTo(0, 0); x.lineTo(7, -4); x.stroke(); }
      x.restore();
    });
    // crowd
    const amp = RM ? 0 : st.cheer * 4;
    for (let r = 0; r < 2; r++) for (let i = 0; i < 12; i++) {
      const cy = H - 50 + r * 20 + (amp ? Math.abs(Math.sin(now / 90 + i)) * -amp : 0);
      x.fillStyle = ["#D98857", "#B96A45", "#E8B26A", "#8C4F2E"][(i + r) % 4];
      x.beginPath(); x.arc(30 + i * 32, cy, 4.4, 0, Math.PI * 2); x.fill();
    }
    st.cheer *= 0.97;
    // dhol players
    x.fillStyle = "#B96A45";
    x.beginPath(); x.arc(20, H * 0.60, 5, 0, Math.PI * 2); x.fill();
    x.beginPath(); x.arc(34, H * 0.60 + (amp ? Math.sin(now / 80) * 2 : 0), 5, 0, Math.PI * 2); x.fill();
    x.fillStyle = "#8B5A2B"; x.fillRect(18, H * 0.60 + 6, 22, 9);
    // team boards — batter side (left) shows the team you picked, bowler side shows the rival
    x.font = "900 9px sans-serif"; x.textAlign = "center";
    x.fillStyle = st.team === "urban" ? "#000" : "#F0C742";
    x.fillText(st.team === "urban" ? "URBAN" : "REGAL", 40, H * 0.50 + 10);
    x.fillStyle = st.team === "urban" ? "#F0C742" : "#000";
    x.fillText(st.team === "urban" ? "REGAL" : "URBAN", W - 40, H * 0.50 + 10);
    // stumps behind the batter (left end) — fly apart when hit
    x.fillStyle = "#F5E9C9";
    if (st.stumpsHit) {
      x.save(); x.translate(BAT_X - 26, PITCH_Y - 20); x.rotate(-0.7); x.fillRect(0, 0, 3, 30); x.restore();
      x.save(); x.translate(BAT_X - 18, PITCH_Y - 26); x.rotate(0.5); x.fillRect(0, 0, 3, 30); x.restore();
      x.fillRect(BAT_X - 34, PITCH_Y - 12, 3, 18);
    } else {
      [-34, -27, -20].forEach((o) => x.fillRect(BAT_X + o, PITCH_Y - 34, 3, 34));
      x.fillRect(BAT_X - 35, PITCH_Y - 36, 19, 2.5);
    }
    // BOWLER — right crease, facing LEFT toward the batter, real run-up
    const bImg = imgs.current[`${st.team === "urban" ? "regal" : "urban"}_bowl`];
    let bowlX = BOWL_X + 58, bob = 0;
    if (st.mode === "runup") {
      const p = Math.min(1, (now - st.modeAt) / st.runupMs);
      bowlX = BOWL_X + 58 - p * 58;
      bob = Math.abs(Math.sin(p * Math.PI * 4)) * 5;
    } else if (st.mode === "flight" || st.mode === "result") bowlX = BOWL_X;
    if (bImg?.complete && bImg.naturalWidth) {
      const bh = 116, bw = bh * (bImg.naturalWidth / bImg.naturalHeight);
      x.drawImage(bImg, bowlX - bw / 2, PITCH_Y - bh - bob, bw, bh);
    }
    // BATTER — left crease, FLIPPED to face RIGHT toward the bowler
    const pImg = imgs.current[`${st.team}_bat`];
    if (pImg?.complete && pImg.naturalWidth) {
      const ph = 126, pw = ph * (pImg.naturalWidth / pImg.naturalHeight);
      x.save();
      x.translate(BAT_X + 8, PITCH_Y);
      let rot = 0, jump = 0;
      if (st.swing > 0) rot = 0.6 * Math.sin(Math.min(1, (now - st.swingAt) / 240) * Math.PI);
      if (st.celebrate) jump = RM ? 0 : Math.abs(Math.sin(now / 160)) * -14;
      if (st.sad) rot = -0.3;
      x.rotate(rot);
      x.scale(-1, 1); // face the bowler
      const idleBob = st.mode === "flight" || st.mode === "runup" ? Math.sin(now / 260) * 2 : 0;
      x.drawImage(pImg, -pw / 2, -ph + idleBob + jump, pw, ph);
      x.restore();
    }
    // timing ring at the contact point — tap when it turns gold
    if (st.mode === "flight" && st.ball) {
      const rem = st.contactAt - now;
      const rr = Math.max(7, Math.min(46, 7 + (rem / st.curDel.ms) * 46));
      const inWin = Math.abs(rem) <= st.window;
      x.strokeStyle = inWin ? "#F0C742" : "rgba(255,255,255,0.45)";
      x.lineWidth = inWin ? 3 : 1.5;
      x.beginPath(); x.arc(CONTACT_X + 10, st.contactY, rr, 0, Math.PI * 2); x.stroke();
    }
    // guide (easy): dashed line along the ball's path
    if (st.guide && st.mode === "flight") {
      x.strokeStyle = "rgba(240,199,66,0.35)"; x.setLineDash([4, 6]);
      x.beginPath(); x.moveTo(RELEASE.x, RELEASE.y); x.lineTo(CONTACT_X + 10, st.contactY); x.stroke();
      x.setLineDash([]);
    }
    // ball + shadow (with a fading trail after a struck boundary)
    if (st.ball) {
      const b = st.ball;
      if (st.flyTrail?.length) {
        st.flyTrail.forEach((t, i) => {
          x.globalAlpha = ((i + 1) / st.flyTrail.length) * 0.35;
          x.fillStyle = "#B22222";
          x.beginPath(); x.arc(t.x, t.y, 4.2, 0, Math.PI * 2); x.fill();
        });
        x.globalAlpha = 1;
      }
      x.fillStyle = "rgba(0,0,0,0.3)";
      x.beginPath(); x.ellipse(b.x, PITCH_Y + 2, 6, 2.4, 0, 0, Math.PI * 2); x.fill();
      x.fillStyle = "#B22222";
      x.beginPath(); x.arc(b.x, b.y, 5.4, 0, Math.PI * 2); x.fill();
      x.strokeStyle = "#F5E9C9"; x.lineWidth = 1;
      x.beginPath(); x.arc(b.x, b.y, 5.4, 0.4, 2.2); x.stroke();
    }
    // hit flash
    if (st.flash > 0) {
      x.globalAlpha = st.flash;
      x.fillStyle = "#FFE9A0";
      x.beginPath(); x.arc(CONTACT_X + 14, st.contactY || PITCH_Y - 30, 14 * (1.4 - st.flash), 0, Math.PI * 2); x.fill();
      x.globalAlpha = 1;
      st.flash -= 0.06;
    }
    // pops
    (st.pops || []).forEach((p) => {
      x.globalAlpha = Math.max(0, Math.min(1, p.life));
      x.font = p.big ? "900 26px Outfit, sans-serif" : "900 15px Outfit, sans-serif";
      x.textAlign = "center";
      x.fillStyle = p.color;
      if (p.big) { x.shadowColor = "#000"; x.shadowBlur = 8; }
      x.fillText(p.text, p.x, p.y - (1 - Math.min(1, p.life)) * 30);
      x.shadowBlur = 0;
      p.life -= 0.016;
    });
    st.pops = (st.pops || []).filter((p) => p.life > 0);
    x.globalAlpha = 1;
    if (st.swing > 0 && now - st.swingAt > 260) st.swing = 0;
    rafRef.current = requestAnimationFrame(draw);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pop = (text, big = false, color = "#F0C742") => {
    g.current?.pops.push({ text, big, color, x: W / 2, y: big ? 190 : 160, life: 1.15 });
  };

  const finishMatch = async (golden) => {
    const st = g.current;
    st.mode = "over";
    try {
      const r = await api.post("/arcade/cricket/submit", {
        user_id: USER_ID, session_id: st.session, balls: st.results, golden });
      setSummary(r.data);
      if (r.data.won) { cheerArp(); coinRain(40); st.celebrate = true; }
      else st.sad = true;
      if (r.data.mela_result?.awarded) melaToast(r.data.mela_result.message);
      badgeToast(r.data.new_badges);
      refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the match");
      const rs = st.results;
      setSummary({
        score: st.score, target: st.target, won: st.score >= st.target, team: st.team, difficulty: diff, unsaved: true,
        fours: rs.filter((b) => b.runs === 4).length, sixes: rs.filter((b) => b.runs === 6).length,
        perfects: rs.filter((b) => b.timing === "perfect").length,
      });
    }
    setStage("done");
  };

  const nextBall = () => {
    const st = g.current;
    if (!st || st.finished) return;
    if (st.score >= st.target) { st.finished = true; finishMatch(st.ballIdx > 6); return; }
    if (st.wickets >= 2 || st.ballIdx >= 6) {
      if (st.ballIdx === 6 && st.wickets < 2 && st.score === st.target - 1 && !st.goldenDone) {
        st.goldenDone = true;
        setHud((h) => ({ ...h, golden: true, comment: "The Golden Ball decides the winner!" }));
        pop("GOLDEN BALL!", true, "#F0C742");
      } else { st.finished = true; finishMatch(st.ballIdx > 6); return; }
    }
    const del = st.deliveries[st.ballIdx];
    st.curDel = del;
    st.mode = "runup";
    st.modeAt = performance.now();
    st.runupMs = 900;
    st.stumpsHit = false;
    st.committed = false;
    st.flyTrail = [];
    st.contactY = PITCH_Y - 30 + (del.line === "off" ? -14 : del.line === "leg" ? 12 : 0);
    setTimeout(() => {
      if (!g.current || g.current.finished) return;
      flickThwip();
      st.mode = "flight";
      st.releaseAt = performance.now();
      st.contactAt = st.releaseAt + del.ms;
      st.ball = { x: RELEASE.x, y: RELEASE.y };
      const anim = () => {
        const s2 = g.current;
        if (!s2 || s2.mode !== "flight") return;
        const p = Math.min(1, (performance.now() - s2.releaseAt) / del.ms);
        s2.ball = { x: RELEASE.x - p * (RELEASE.x - CONTACT_X), y: ballY(p, del, s2.contactY) };
        if (p >= 1) { if (!s2.committed) commit(null, 0, true); return; }
        s2.flightTimer = requestAnimationFrame(anim);
      };
      anim();
    }, st.runupMs);
  };

  const commit = (dir, power, autoMiss = false) => {
    const st = g.current;
    if (!st || st.mode !== "flight" || st.committed) return;
    st.committed = true;
    st.mode = "result";
    cancelAnimationFrame(st.flightTimer);
    const del = st.deliveries[st.ballIdx];
    const offset = autoMiss ? st.window * 2.5 : performance.now() - st.contactAt;
    const res = autoMiss
      ? judge(st.window * 2.5, "defend", del, 0, st.window)
      : judge(offset, dir, del, power, st.window);
    st.swing = autoMiss ? 0 : 1;
    st.swingAt = performance.now();
    if (!autoMiss && res.timing !== "miss") { whipCrack(); st.flash = 1; }
    st.ballIdx += 1;
    st.results.push({ runs: res.runs, out: res.out, timing: res.timing });
    if (res.out) {
      clonk();
      st.wickets += 1;
      if (res.how === "BOWLED" || res.how === "LBW") st.stumpsHit = true;
      st.ball = null;
      pop(res.how + "!", true, "#C0392B");
    } else {
      st.score += res.runs;
      if (res.runs >= 4) {
        cheerArp();
        st.cheer = 1;
        pop(res.runs === 6 ? "SIX!" : "FOUR!", true, res.runs === 6 ? "#F0C742" : "#8FE3A5");
        pop(PA_POPS[Math.floor(Math.random() * PA_POPS.length)], false, "#F0C742");
        // ball flies in the direction of the shot
        const ang = dir === "leg" ? Math.PI * (0.75 + Math.random() * 0.3)      // behind square, left
          : dir === "loft" ? -Math.PI * (0.25 + Math.random() * 0.15)           // lofted up-right
          : -Math.PI * (Math.random() * 0.18);                                  // driven right past the bowler
        const b = st.ball || { x: CONTACT_X + 10, y: st.contactY };
        st.ball = b;
        st.flyTrail = [];
        const fly = () => {
          const s2 = g.current;
          if (!s2?.ball) return;
          s2.flyTrail.push({ x: b.x, y: b.y });
          if (s2.flyTrail.length > 9) s2.flyTrail.shift();
          b.x += Math.cos(ang) * 6.5;
          b.y += Math.sin(ang) * 6.5;
          if (b.y > -20 && b.y < H + 20 && b.x > -20 && b.x < W + 20) requestAnimationFrame(fly);
          else { s2.ball = null; s2.flyTrail = []; }
        };
        fly();
      } else {
        if (res.runs > 0) pop(`+${res.runs}`, false, "#8FE3A5");
        st.ball = null;
      }
      if (res.timing === "perfect") knockPing();
    }
    const need = Math.max(0, st.target - st.score);
    const pool = res.out ? COMMENT.out : res.runs >= 4 ? COMMENT[res.runs] : res.runs > 0 ? COMMENT.run : COMMENT.dot;
    setHud({
      score: st.score, wickets: st.wickets, balls: st.ballIdx, target: st.target,
      feedback: res.out ? `${res.how}!` : res.label, golden: st.ballIdx > 6,
      comment: need === 1 && st.ballIdx >= 5 && !res.out ? "One ball. Everything depends on this." : pool[Math.floor(Math.random() * pool.length)],
    });
    setTimeout(nextBall, 1500);
  };

  const startMatch = async () => {
    setBusy(true);
    setSummary(null);
    try {
      const r = await api.post("/arcade/cricket/start", { user_id: USER_ID, team, difficulty: diff });
      g.current = {
        session: r.data.session_id, team, deliveries: r.data.deliveries, window: r.data.window_ms,
        target: r.data.target, score: 0, wickets: 0, ballIdx: 0, results: [], pops: [], cheer: 0,
        mode: "idle", ball: null, swing: 0, flash: 0, celebrate: false, sad: false, finished: false, flyTrail: [],
        goldenDone: false, guide: diff === "easy", curDel: r.data.deliveries[0], contactY: PITCH_Y - 30,
      };
      setHud({ score: 0, wickets: 0, balls: 0, target: r.data.target, feedback: "", comment: "The mela holds its breath...", golden: false });
      setStage("play");
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(draw);
      setTimeout(() => canvasRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 120);
      setTimeout(nextBall, 900);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start the match"); }
    setBusy(false);
  };

  const onDown = (e) => {
    e.preventDefault();
    const st = g.current;
    if (!st) return;
    st.press = { t: performance.now(), x: e.clientX, y: e.clientY };
  };
  const onUp = (e) => {
    const st = g.current;
    if (!st?.press) return;
    const dx = e.clientX - st.press.x, dy = e.clientY - st.press.y;
    const len = Math.hypot(dx, dy);
    const dir = len < 16 ? "straight" : Math.abs(dx) > Math.abs(dy) ? (dx < 0 ? "leg" : "off") : (dy < 0 ? "loft" : "defend");
    const power = len < 16 ? 0.7 : Math.min(1, 0.35 + len / 150);
    st.press = null;
    commit(dir, power);
  };

  useEffect(() => {
    if (stage !== "play") return;
    const onKey = (e) => {
      const st = g.current;
      if (!st) return;
      if (e.code === "ArrowLeft") { st.pendDir = "leg"; e.preventDefault(); }
      else if (e.code === "ArrowRight") { st.pendDir = "off"; e.preventDefault(); }
      else if (e.code === "ArrowUp") { st.pendDir = "loft"; e.preventDefault(); }
      else if (e.code === "ArrowDown") { st.pendDir = "defend"; e.preventDefault(); }
      else if (e.code === "Space") { e.preventDefault(); commit(st.pendDir || "straight", 0.8); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [stage]); // eslint-disable-line react-hooks/exhaustive-deps

  const need = Math.max(0, hud.target - hud.score);

  return (
    <div data-testid="game-cricket" className="rounded-2xl bg-[#121212] border border-[#F0C742]/40 p-5">
      <p className="text-[10px] tracking-[0.3em] text-[#F0C742] font-bold">URBAN vs REGAL · CRICKET CLASH</p>
      <p className="text-xs text-white/50 mt-1 italic">"Two icons. One pitch. One final over."</p>

      {stage === "intro" && (
        <div data-testid="cricket-intro" className="relative mt-4 rounded-xl overflow-hidden border border-[#F0C742]/30 bg-gradient-to-b from-[#231433] via-[#4A2338] to-[#1A1208] p-6 text-center">
          <div className="flex items-end justify-center gap-6">
            <img src="/cricket/urban_bat.png" alt="Urban Mascot" className={`h-36 w-auto -scale-x-100 ${RM ? "" : "cricket-enter-left"}`} />
            <img src="/cricket/regal_bat.png" alt="Mr. Regal" className={`h-40 w-auto ${RM ? "" : "cricket-enter-right"}`} />
          </div>
          <p className="mt-4 font-display text-3xl font-black text-[#F0C742] drop-shadow">URBAN vs REGAL</p>
          <p className="text-[11px] tracking-[0.4em] text-white/70 font-black">CRICKET CLASH</p>
          <p className="text-[11px] text-white/50 mt-2">The mela lights turn on... the dhol begins... a gold coin spins in the air.</p>
          <button data-testid="cricket-play-now" onClick={() => { localStorage.setItem(INTRO_KEY, "1"); setStage("pick"); }}
            className="mt-4 h-12 px-10 rounded-full bg-[#F0C742] text-black font-black text-sm tracking-widest animate-pulse">PLAY NOW</button>
        </div>
      )}

      {stage === "pick" && (
        <div data-testid="cricket-pick" className="mt-4">
          <p className="text-[9px] tracking-[0.25em] text-white/50 font-black">PICK YOUR TEAM</p>
          <div className="grid grid-cols-2 gap-2.5 mt-2">
            {[["urban", "TEAM URBAN", "Urban Mascot — fast, playful, all heart", "#D4AF37"],
              ["regal", "TEAM REGAL", "Mr. Regal — tall, composed, pure class", "#B4653A"]].map(([k, n, d, c]) => (
              <button key={k} data-testid={`cricket-team-${k}`} onClick={() => setTeam(k)}
                className={`rounded-xl border p-3 text-left transition-colors ${team === k ? "border-[#F0C742] bg-[#1C160A]" : "border-white/10 bg-[#14100A]"}`}>
                <img src={`/cricket/${k}_bat.png`} alt={n} className="h-20 w-auto mx-auto" />
                <p className="text-[10px] font-black mt-1.5" style={{ color: c }}>{n}</p>
                <p className="text-[9px] text-white/45">{d}</p>
              </button>
            ))}
          </div>
          <p className="text-[9px] tracking-[0.25em] text-white/50 font-black mt-4">DIFFICULTY</p>
          <div className="space-y-1.5 mt-2">
            {DIFFS.map((d) => (
              <button key={d.key} data-testid={`cricket-diff-${d.key}`} onClick={() => setDiff(d.key)}
                className={`w-full rounded-xl border px-3.5 py-2.5 text-left flex items-center justify-between gap-2 ${diff === d.key ? "border-[#F0C742] bg-[#1C160A]" : "border-white/10 bg-[#14100A]"}`}>
                <span>
                  <span className="text-[11px] font-black">{d.name}</span>
                  <span className="block text-[9px] text-white/45">{d.desc}</span>
                </span>
                {diff === d.key && <span className="text-[8px] font-black text-black bg-[#F0C742] rounded-full px-2 py-0.5">PICKED</span>}
              </button>
            ))}
            {ustaadUnlocked ? (
              <button data-testid="cricket-diff-ustaad" onClick={() => setDiff("ustaad")}
                className={`w-full rounded-xl border px-3.5 py-2.5 text-left flex items-center justify-between gap-2 ${diff === "ustaad" ? "border-[#F0C742] bg-[#1C160A]" : "border-[#F0C742]/25 bg-[#14100A]"}`}>
                <span>
                  <span className="text-[11px] font-black text-[#F0C742] flex items-center gap-1.5"><Trophy size={10} /> CRICKET USTAAD</span>
                  <span className="block text-[9px] text-white/45">Lightning swing bowling — the final test, unlocked by your Punjab Pro win</span>
                </span>
                {diff === "ustaad" && <span className="text-[8px] font-black text-black bg-[#F0C742] rounded-full px-2 py-0.5">PICKED</span>}
              </button>
            ) : (
              <div data-testid="cricket-diff-expert" className="w-full rounded-xl border border-white/10 bg-[#14100A]/60 px-3.5 py-2.5 flex items-center justify-between opacity-60">
                <span>
                  <span className="text-[11px] font-black flex items-center gap-1.5">CRICKET USTAAD <Lock size={10} /></span>
                  <span className="block text-[9px] text-white/45">Unlocks after you win a Punjab Pro match</span>
                </span>
              </div>
            )}
          </div>
          <button data-testid="cricket-start" onClick={startMatch} disabled={busy}
            className="mt-4 w-full h-12 rounded-full bg-[#F0C742] text-black font-black text-sm tracking-widest disabled:opacity-50">
            {busy ? <Loader2 size={15} className="animate-spin mx-auto" /> : "WALK TO THE PITCH"}
          </button>
        </div>
      )}

      {(stage === "play" || stage === "done") && (
        <>
          <div className="mt-4 rounded-lg bg-[#1A1208] border border-[#8a6d1f]/40 px-3 py-2 flex items-center justify-between text-[10px] font-black flex-wrap gap-y-1">
            <span data-testid="cricket-score" className="text-[#F0C742]">{hud.score}/{hud.wickets}</span>
            <span data-testid="cricket-target" className="text-white/60">TARGET {hud.target}</span>
            <span data-testid="cricket-need" className={need <= 6 && need > 0 ? "text-[#E25822]" : "text-white/60"}>NEED {need}</span>
            <span data-testid="cricket-balls" className="text-white/60">{hud.golden ? "GOLDEN BALL" : `BALLS ${6 - Math.min(6, hud.balls)}`}</span>
            <span className="text-white/40 uppercase">{diff}</span>
          </div>
          <div className="relative mt-2 rounded-xl overflow-hidden border border-white/10 select-none max-w-[460px] mx-auto">
            <canvas ref={canvasRef} width={W} height={H} data-testid="cricket-canvas"
              className="w-full block touch-none cursor-pointer"
              onPointerDown={onDown} onPointerUp={onUp} />
            {hud.feedback && stage === "play" && (
              <span data-testid="cricket-feedback"
                className={`absolute top-2 left-1/2 -translate-x-1/2 text-[10px] font-black tracking-[0.25em] px-3 py-1 rounded-full ${hud.feedback === "PERFECT" ? "bg-[#F0C742] text-black" : "bg-black/60 text-white/80"}`}>
                {hud.feedback}
              </span>
            )}
          </div>
          {stage === "play" && (
            <>
              <p data-testid="cricket-comment" className="text-[11px] text-[#F0C742]/80 font-bold text-center mt-2 min-h-[16px]">{hud.comment}</p>
              <p className="text-[9px] text-white/35 text-center mt-1">TAP WHEN THE RING TURNS GOLD · SWIPE ← LEG · → DRIVE · ↑ LOFT · ↓ DEFEND</p>
            </>
          )}
        </>
      )}

      {stage === "done" && summary && (
        <div data-testid="cricket-summary" className="mt-3 rounded-xl border border-[#D4AF37]/40 bg-[#1C160A] p-4 text-center">
          <p className={`text-[10px] tracking-[0.3em] font-black ${summary.won ? "text-[#F0C742] animate-pulse" : "text-white/50"}`}>
            {summary.won ? "VICTORY! ਚੱਕ ਦੇ ਫੱਟੇ!" : "THE CHASE FALLS SHORT"}
          </p>
          <p className="font-display text-3xl font-bold mt-1">{summary.score} <span className="text-white/40 text-lg">/ {summary.target}</span></p>
          <div className="flex items-center justify-center gap-4 text-[10px] font-black text-white/60 mt-2">
            <span data-testid="cricket-sum-fours">FOURS {summary.fours}</span>
            <span data-testid="cricket-sum-sixes">SIXES {summary.sixes}</span>
            <span data-testid="cricket-sum-perfects">PERFECT {summary.perfects}</span>
          </div>
          {summary.unsaved && <p data-testid="cricket-unsaved" className="text-[10px] text-[#C0392B] font-black mt-2">MATCH NOT SAVED — no coins or badges this time</p>}
          {summary.mela_result && (
            <p data-testid="cricket-mela-line" className="text-xs text-[#F0C742] font-bold mt-2 flex items-center justify-center gap-1.5">
              <MelaCoin size={15} /> {summary.won && !summary.mela_result.awarded
                ? "Coin cap reached for today — this win is for pure glory! ਸ਼ਾਬਾਸ਼!"
                : summary.mela_result.message}
            </p>
          )}
          {!summary.unsaved && !summary.mela_result && <p data-testid="cricket-no-coins" className="text-[10px] text-white/40 mt-2">No Regal Coins this over — score at least one run to earn coins.</p>}
          <p className="text-[10px] text-white/50 mt-3">
            {summary.won
              ? (summary.team === "urban" ? "Urban Mascot recommends a proper victory meal." : "Celebrate like royalty with Mr. Regal's featured selection.")
              : "Even legends lose an over — the tandoor is still hot."}
          </p>
          <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
            <button data-testid="cricket-again" onClick={startMatch}
              className="h-10 px-5 rounded-full bg-[#F0C742] text-black text-[10px] font-black tracking-widest">PLAY AGAIN</button>
            {summary.won && (
              <button data-testid="cricket-share"
                onClick={() => shareCricketWin(summary).then((m) => m === "downloaded" && toast("Win card downloaded — share it anywhere!", { style: { background: "#1A1A1A", color: "#D4AF37" } })).catch(() => {})}
                className="h-10 px-5 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#F0C742] text-black text-[10px] font-black tracking-widest inline-flex items-center gap-1.5">
                <Share2 size={12} /> SHARE WIN
              </button>
            )}
            <button data-testid="cricket-change-team" onClick={() => setStage("pick")}
              className="h-10 px-5 rounded-full border border-[#F0C742]/50 text-[#F0C742] text-[10px] font-black tracking-widest">CHANGE TEAM</button>
            <Link data-testid="cricket-order-food" to="/experience/menu"
              className="h-10 px-5 rounded-full border border-[#E25822]/60 text-[#E25822] text-[10px] font-black tracking-widest inline-flex items-center gap-1.5"><Utensils size={12} /> ORDER FOOD</Link>
          </div>
        </div>
      )}
    </div>
  );
};
